import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { authrulesApi } from "@/api/authrules.api";
import {
    Alert, Badge, Field, Input, Select, Btn, Modal, Th, Td, Empty, Tabs, limpia,
} from "./authrulesUi";

// ============================================================================
//  ConfiguracionCuentas — relacion id_cliente (blaster) <-> empresa
//  + catalogo de IPs por cuenta.
// ============================================================================

const TABS = [
    { id: "blaster", label: "Relacion blaster" },
    { id: "catalogo", label: "Catalogo de IPs" },
];

// ── Relacion blaster (mapeo inline) ──────────────────────────────────────────

function BlasterTab() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");

    const cargar = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await authrulesApi.blaster({ accion: "listar" });
            setRows(res.data || []);
            setEmpresas(res.empresas || []);
            setCustomers(res.customers || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const { hijosDe, padres } = useMemo(() => {
        const byId = {};
        rows.forEach((r) => { byId[r.id_cliente] = r; });
        const hijosDe = {};
        const padres = [];
        rows.forEach((r) => {
            if (r.parent_id && byId[r.parent_id]) {
                (hijosDe[r.parent_id] = hijosDe[r.parent_id] || []).push(r);
            } else {
                padres.push(r);
            }
        });
        const porId = (a, b) => String(a.id_cliente).localeCompare(String(b.id_cliente));
        padres.sort(porId);
        Object.values(hijosDe).forEach((arr) => arr.sort(porId));
        return { hijosDe, padres };
    }, [rows]);

    const toggle = (id) =>
        setExpanded((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });

    const visibles = useMemo(() => {
        const s = q.trim().toLowerCase();
        const searching = !!s;
        const match = (r) =>
            !s || [r.id_cliente, r.nombre, r.razon_social].some((v) => String(v || "").toLowerCase().includes(s));
        const out = [];
        for (const p of padres) {
            const kids = hijosDe[p.id_cliente] || [];
            if (searching) {
                const pM = match(p);
                const kM = kids.filter(match);
                if (pM) {
                    out.push({ r: p, depth: 0, kids, open: true });
                    kids.forEach((k) => out.push({ r: k, depth: 1 }));
                } else if (kM.length) {
                    out.push({ r: p, depth: 0, kids, open: true, ctx: true });
                    kM.forEach((k) => out.push({ r: k, depth: 1 }));
                }
            } else {
                const open = expanded.has(p.id_cliente);
                out.push({ r: p, depth: 0, kids, open });
                if (open) kids.forEach((k) => out.push({ r: k, depth: 1 }));
            }
        }
        return out;
    }, [padres, hijosDe, q, expanded]);

    // Mapear/desmapear EMPRESA (Clearcom); si es padre, arrastra a sus hijos.
    const asignar = async (row, empresaId) => {
        const kids = hijosDe[row.id_cliente] || [];
        const targets = [row, ...kids];
        setError(""); setOk(""); setSavingId(row.id_cliente);
        try {
            for (const t of targets) {
                if (!empresaId) {
                    await authrulesApi.blaster({ accion: "quitar", id_cliente: t.id_cliente, campo: "empresa_id" });
                } else {
                    await authrulesApi.blaster({ accion: "crear", id_cliente: t.id_cliente, empresa_id: empresaId });
                }
            }
            const extra = kids.length ? ` (+${kids.length} hijos)` : "";
            setOk(`${row.id_cliente} ${empresaId ? "mapeado" : "desmapeado"}${extra}.`);
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingId(null);
        }
    };

    // Mapear/desmapear CUSTOMER (Netbox/PortaBilling); solo esa fila.
    const asignarCustomer = async (row, iCustomer) => {
        setError(""); setOk(""); setSavingId(row.id_cliente);
        try {
            if (!iCustomer) {
                await authrulesApi.blaster({ accion: "quitar", id_cliente: row.id_cliente, campo: "i_customer" });
            } else {
                await authrulesApi.blaster({ accion: "crear", id_cliente: row.id_cliente, i_customer: iCustomer });
            }
            setOk(`${row.id_cliente} ${iCustomer ? "ligado a Netbox" : "desligado de Netbox"}.`);
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const mapeados = rows.filter((r) => r.empresa_id).length;

    return (
        <div>
            <Alert kind="error" onClose={() => setError("")}>{error}</Alert>
            <Alert kind="ok" onClose={() => setOk("")}>{ok}</Alert>

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="min-w-64 flex-1">
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por id_cliente, nombre o razon social…" />
                </div>
                <span className="text-sm text-slate-400">{mapeados}/{rows.length} mapeados</span>
            </div>

            {rows.length === 0 && !loading ? (
                <Empty>No hay clientes en clearcom.blaster_clientes.</Empty>
            ) : visibles.length === 0 ? (
                <Empty>Sin coincidencias.</Empty>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <Th>id_cliente</Th>
                                <Th>nombre</Th>
                                <Th>razon social</Th>
                                <Th>empresa (tuya)</Th>
                                <Th>customer (netbox)</Th>
                                <Th>estado</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map(({ r, depth, kids, open, ctx }) => {
                                const esPadre = depth === 0;
                                const tieneHijos = esPadre && kids && kids.length > 0;
                                return (
                                    <tr
                                        key={r.id_cliente}
                                        className={
                                            depth > 0
                                                ? "bg-slate-50/60 hover:bg-slate-100"
                                                : tieneHijos
                                                    ? "border-l-2 border-teal-400 bg-teal-50/30 hover:bg-teal-50/60"
                                                    : "hover:bg-slate-50"
                                        }
                                    >
                                        <Td mono>{r.id_cliente}</Td>
                                        <Td>
                                            <div style={{ paddingLeft: depth * 22 }} className="flex items-center gap-1.5">
                                                {tieneHijos ? (
                                                    <button
                                                        onClick={() => toggle(r.id_cliente)}
                                                        className="grid h-5 w-5 place-items-center rounded text-slate-500 hover:bg-slate-200"
                                                        aria-label={open ? "Colapsar" : "Expandir"}
                                                    >
                                                        {open ? "▾" : "▸"}
                                                    </button>
                                                ) : (
                                                    <span className="inline-block w-5">{depth > 0 ? <span className="text-slate-300">└</span> : null}</span>
                                                )}
                                                <span className={esPadre ? "font-semibold text-slate-800" : "text-slate-600"}>{r.nombre || "—"}</span>
                                                {tieneHijos && <Badge tone="teal">{kids.length} hijos</Badge>}
                                                {ctx && <span className="text-xs italic text-slate-400">(padre)</span>}
                                            </div>
                                        </Td>
                                        <Td>{r.razon_social || "—"}</Td>
                                        <Td>
                                            <Select
                                                value={r.empresa_id || ""}
                                                onChange={(e) => asignar(r, e.target.value)}
                                                disabled={savingId === r.id_cliente}
                                                className="min-w-44"
                                            >
                                                <option value="">— sin mapear —</option>
                                                {empresas.map((emp) => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
                                            </Select>
                                        </Td>
                                        <Td>
                                            <Select
                                                value={r.i_customer || ""}
                                                onChange={(e) => asignarCustomer(r, e.target.value)}
                                                disabled={savingId === r.id_cliente}
                                                className="min-w-44"
                                            >
                                                <option value="">— sin ligar —</option>
                                                {customers.map((c) => (
                                                    <option key={c.i_customer} value={c.i_customer}>
                                                        {c.nombre} ({c.i_customer})
                                                    </option>
                                                ))}
                                            </Select>
                                        </Td>
                                        <Td>
                                            {savingId === r.id_cliente ? (
                                                <Badge tone="amber">guardando…</Badge>
                                            ) : (
                                                <div className="flex flex-wrap gap-1">
                                                    {r.empresa_id && <Badge tone="teal">Clearcom</Badge>}
                                                    {r.i_customer && <Badge tone="teal">Netbox</Badge>}
                                                    {!r.empresa_id && !r.i_customer && <Badge>sin mapear</Badge>}
                                                </div>
                                            )}
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Catalogo de IPs ──────────────────────────────────────────────────────────

function CatalogoTab() {
    const [filtros, setFiltros] = useState({ empresa_id: "", q: "" });
    const [orden, setOrden] = useState("asc");
    const [page, setPage] = useState(1);
    const [limit] = useState(15);

    const [empresas, setEmpresas] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [expanded, setExpanded] = useState(() => new Set());
    const [ipsPorCuenta, setIpsPorCuenta] = useState({});
    const [savingAcc, setSavingAcc] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [modal, setModal] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await authrulesApi.opciones({ todas: 1 });
                setEmpresas(res.empresas || []);
            } catch (e) {
                setError(e.message);
            }
        })();
    }, []);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await authrulesApi.catalogo({
                accion: "cuentas",
                empresa_id: filtros.empresa_id || undefined,
                q: filtros.q || undefined,
                page,
                limit,
                orden,
            });
            setCuentas(res.data || []);
            setTotal(res.total || 0);
            setTotalPages(res.total_pages || 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [filtros, page, limit, orden]);

    const sincronizar = async () => {
        setLoading(true);
        setError("");
        try {
            await authrulesApi.catalogo({ accion: "sincronizar" });
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(cargar, 250);
        return () => clearTimeout(t);
    }, [cargar]);

    useEffect(() => { setPage(1); }, [filtros, orden]);

    const fetchIps = async (i_account) => {
        const res = await authrulesApi.catalogo({ accion: "listar", i_account });
        setIpsPorCuenta((m) => ({ ...m, [i_account]: res.data || [] }));
    };

    const toggle = async (i_account) => {
        const next = new Set(expanded);
        if (next.has(i_account)) {
            next.delete(i_account);
        } else {
            next.add(i_account);
            if (!ipsPorCuenta[i_account]) await fetchIps(i_account);
        }
        setExpanded(next);
    };

    const mapear = async (acc, empresaId) => {
        if (!empresaId) return;
        setError(""); setOk(""); setSavingAcc(acc.i_account);
        try {
            await authrulesApi.catalogo({ accion: "mapear", i_account: acc.i_account, empresa_id: empresaId });
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingAcc(null);
        }
    };

    const desmapear = async (acc, empresaId) => {
        setError(""); setOk(""); setSavingAcc(acc.i_account);
        try {
            await authrulesApi.catalogo({ accion: "desmapear", i_account: acc.i_account, empresa_id: empresaId });
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingAcc(null);
        }
    };

    const guardar = async (form) => {
        setError(""); setOk("");
        try {
            if (modal.mode === "editar") {
                await authrulesApi.catalogo({ accion: "editar", id: modal.data.catalogo_id, descripcion: form.descripcion, activo: form.activo });
                setOk("IP actualizada.");
            } else {
                await authrulesApi.catalogo({ accion: "crear", i_account: form.i_account, remote_ip: form.remote_ip, descripcion: form.descripcion });
                setOk("IP guardada en catalogo.");
            }
            setModal(null);
            if (form.i_account) await fetchIps(Number(form.i_account));
        } catch (e) {
            setError(e.message);
        }
    };

    const quitar = async (r) => {
        if (!r.catalogo_id) return;
        if (!window.confirm(`Quitar ${r.remote_ip} del catalogo? (no toca Sippy)`)) return;
        try {
            await authrulesApi.catalogo({ accion: "borrar", id: r.catalogo_id });
            setOk("Quitada del catalogo.");
            await fetchIps(r.i_account);
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <div>
            <Alert kind="error" onClose={() => setError("")}>{error}</Alert>
            <Alert kind="ok" onClose={() => setOk("")}>{ok}</Alert>

            <p className="mb-3 text-sm text-slate-500">
                Cuentas de Sippy con IPs vivas (sippy_authrules). Una cuenta puede mapear a varias empresas (onepbx); expande para ver sus IPs.
            </p>

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Field label="empresa">
                    <Select value={filtros.empresa_id} onChange={(e) => setFiltros({ ...filtros, empresa_id: e.target.value })} className="min-w-48">
                        <option value="">Todas</option>
                        {empresas.map((emp) => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
                    </Select>
                </Field>
                <div className="min-w-56 flex-1"><Field label="buscar"><Input value={filtros.q} onChange={(e) => setFiltros({ ...filtros, q: e.target.value })} placeholder="IP, cuenta o empresa…" /></Field></div>
                <Btn variant="ghost" onClick={() => { setPage(1); setOrden((o) => (o === "asc" ? "desc" : "asc")); }}>
                    cuenta {orden === "asc" ? "↑ asc" : "↓ desc"}
                </Btn>
                <Btn variant="ghost" onClick={sincronizar} disabled={loading}>
                    Sincronizar IPs
                </Btn>
                <span className="ml-auto text-sm text-slate-400">{total} cuentas</span>
            </div>

            {cuentas.length === 0 && !loading ? (
                <Empty>Sin cuentas para estos filtros.</Empty>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr><Th>cuenta</Th><Th>empresa (tuya)</Th><Th>ips</Th><Th>reglas</Th><Th>estado</Th></tr>
                            </thead>
                            <tbody>
                                {cuentas.map((acc) => {
                                    const open = expanded.has(acc.i_account);
                                    const ips = ipsPorCuenta[acc.i_account] || [];
                                    return (
                                        <Fragment key={acc.i_account}>
                                            <tr className={open ? "bg-teal-50/40" : "hover:bg-slate-50"}>
                                                <Td>
                                                    <div className="flex items-center gap-1.5">
                                                        <button onClick={() => toggle(acc.i_account)} className="grid h-5 w-5 place-items-center rounded text-slate-500 hover:bg-slate-200" aria-label={open ? "Colapsar" : "Expandir"}>
                                                            {open ? "▾" : "▸"}
                                                        </button>
                                                        <span className="font-medium text-slate-800">{acc.cuenta_nombre || `cuenta ${acc.i_account}`}</span>
                                                        <span className="font-mono text-xs text-slate-400">({acc.i_account})</span>
                                                    </div>
                                                </Td>
                                                <Td>
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        {(acc.empresas || []).map((em) => (
                                                            <span key={em.empresa_id} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700">
                                                                {em.nombre}
                                                                <button
                                                                    onClick={() => desmapear(acc, em.empresa_id)}
                                                                    disabled={savingAcc === acc.i_account}
                                                                    className="leading-none text-teal-500 hover:text-rose-600"
                                                                    aria-label={`Quitar ${em.nombre}`}
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                        <Select
                                                            value=""
                                                            onChange={(e) => mapear(acc, e.target.value)}
                                                            disabled={savingAcc === acc.i_account}
                                                            className="min-w-40"
                                                        >
                                                            <option value="">+ agregar empresa</option>
                                                            {empresas
                                                                .filter((emp) => !(acc.empresas || []).some((x) => String(x.empresa_id) === String(emp.id)))
                                                                .map((emp) => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
                                                        </Select>
                                                    </div>
                                                </Td>
                                                <Td>{acc.ips}</Td>
                                                <Td>{acc.reglas}</Td>
                                                <Td>
                                                    {savingAcc === acc.i_account
                                                        ? <Badge tone="amber">guardando…</Badge>
                                                        : (acc.empresas && acc.empresas.length)
                                                            ? <Badge tone="teal">{acc.empresas.length === 1 ? "mapeada" : `mapeada (${acc.empresas.length})`}</Badge>
                                                            : <Badge>sin mapear</Badge>}
                                                </Td>
                                            </tr>

                                            {open && ips.map((r) => (
                                                <tr key={`${acc.i_account}-${r.remote_ip}`} className="bg-slate-50/60">
                                                    <Td>
                                                        <span className="ml-7 inline-flex items-center gap-1 font-mono text-xs text-slate-600">
                                                            <span className="text-slate-300">└</span> {r.remote_ip}
                                                        </span>
                                                    </Td>
                                                    <Td>
                                                        <div className="flex items-center gap-1">
                                                            <Badge tone="teal">Sippy</Badge>
                                                            {r.catalogo_id ? <Badge tone="slate">catalogo</Badge> : null}
                                                            {r.descripcion ? <span className="text-xs text-slate-400">· {r.descripcion}</span> : null}
                                                        </div>
                                                    </Td>
                                                    <Td>—</Td>
                                                    <Td>{r.reglas}</Td>
                                                    <Td>
                                                        <div className="flex gap-2">
                                                            {r.catalogo_id ? (
                                                                <>
                                                                    <button onClick={() => setModal({ mode: "editar", data: { ...r, descripcion: r.descripcion || "", activo: r.activo ?? 1 } })} className="text-teal-600 hover:underline">Editar</button>
                                                                    <button onClick={() => quitar(r)} className="text-rose-600 hover:underline">Quitar</button>
                                                                </>
                                                            ) : (
                                                                <button onClick={() => setModal({ mode: "crear", data: { i_account: r.i_account, remote_ip: r.remote_ip, descripcion: "" } })} className="text-teal-600 hover:underline">+ catalogo</button>
                                                            )}
                                                        </div>
                                                    </Td>
                                                </tr>
                                            ))}
                                            {open && ips.length === 0 && (
                                                <tr className="bg-slate-50/60"><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-sm text-slate-400">Pagina {page} de {totalPages}</span>
                        <div className="flex gap-2">
                            <Btn variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Anterior</Btn>
                            <Btn variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>Siguiente</Btn>
                        </div>
                    </div>
                </>
            )}

            <CatalogoModal modal={modal} onClose={() => setModal(null)} onSave={guardar} />
        </div>
    );
}

function CatalogoModal({ modal, onClose, onSave }) {
    const [form, setForm] = useState({ i_account: "", remote_ip: "", descripcion: "", activo: 1 });
    useEffect(() => {
        if (modal) setForm({ i_account: "", remote_ip: "", descripcion: "", activo: 1, ...modal.data });
    }, [modal]);
    if (!modal) return null;
    const esEditar = modal.mode === "editar";
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <Modal
            open={!!modal}
            onClose={onClose}
            title={esEditar ? "Editar IP del catalogo" : "Agregar IP al catalogo"}
            footer={
                <>
                    <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
                    <Btn onClick={() => onSave(form)}>{esEditar ? "Guardar" : "Agregar"}</Btn>
                </>
            }
        >
            <div className="grid grid-cols-2 gap-3">
                <Field label="i_account *"><Input value={form.i_account} onChange={set("i_account")} disabled={esEditar} /></Field>
                <Field label="remote_ip *"><Input value={form.remote_ip} onChange={set("remote_ip")} placeholder="50.30.32.69" disabled={esEditar} /></Field>
                <div className="col-span-2"><Field label="descripcion"><Input value={form.descripcion} onChange={set("descripcion")} /></Field></div>
                {esEditar && (
                    <Field label="estado">
                        <Select value={form.activo} onChange={(e) => setForm({ ...form, activo: Number(e.target.value) })}>
                            <option value={1}>activa</option>
                            <option value={0}>inactiva</option>
                        </Select>
                    </Field>
                )}
            </div>
        </Modal>
    );
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function ConfiguracionCuentas() {
    const [tab, setTab] = useState("blaster");

    return (
        <div className="mx-auto max-w-6xl px-4 py-6">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-slate-800">Configuracion de cuentas · Clearcom</h1>
                <p className="text-sm text-slate-500">Relaciona el id_cliente del companero con tus empresas y administra el catalogo de IPs por cuenta.</p>
            </div>

            <Tabs tabs={TABS} active={tab} onChange={setTab} />

            {tab === "blaster" && <BlasterTab />}
            {tab === "catalogo" && <CatalogoTab />}
        </div>
    );
}